<template>
    <div class="flex w-full flex-col flex-1">
        <div class="w-full h-14 rounded-t-lg bg-background-3 border border-interactive-11 flex justify-center items-center text-center" :class="styleClasses.titlePadding">
            <p>Choose an amount to give <span v-if="showDonationFrequencyLabel" class="font-medium underline underline-offset-1 text-orange-700">monthly</span></p>
        </div>
        <div class="w-full flex flex-col flex-1" :class="styleClasses.contentPadding">
            <div class="w-full flex flex-wrap" v-if="loading">
                <div
                    class="skeleton-loader w-[30%] first:ml-0 h-10 text-center p-2.5 rounded-[4px] mt-4 hover:bg-primary-1 hover:text-white cursor-pointer group"
                    :class="styleClasses.preloaderMargin"
                    v-for="i in 3"
                    :key="i"
                ></div>
            </div>
            <div class="w-full flex flex-wrap" :class="styleClasses.donationValuesWrap" v-if="!loading">
                <DonationValues
                    v-for="(donation, index) in donations"
                    :key="donation.id"
                    :index="index"
                    :donation="donation"
                    :selected-donation="selectedDonation"
                    :is-custom-donation="isCustomDonation"
                    @select-donation="selectDonation(donation)"
                    :body-width="bodyWidth"
                />
                <div
                    class="flex-1 relative mt-4"
                    :class="styleClasses.inputWrap"
                >
                    <span class="text-sm absolute top-1/2 left-auto right-6 -translate-y-1/2 text-interactive-6" :class="styleClasses.inputCurrency">USD</span>
                    <input
                        v-model="customDonationValue"
                        type="number"
                        class="w-full p-2.5 rounded-[4px] cursor-pointer border border-interactive-8"
                        :class="styleClasses.input"
                        @input="selectCustomValue"
                    />
                </div>
                <div class="w-full relative">
                    <label class="container flex flex-row mt-4">
                        <input type="checkbox" checked="checked" v-model="userSupportFees">
                        <span class="checkmark flex-shrink-0"></span>
                        <p class="text-xs mt-0 pt-0 ml-2">I'd like to help cover the <span class="font-medium">{{ transactionFee }}</span> transaction fees for my donation.</p>
                    </label>
                    <label class="container flex flex-row mt-4">
                        <p class="text-xs mt-0 pt-0 ml-2 text-[11px]">By pressing "next step" I agree to not receive a gift.<br>If you wish to donate and receive a gift please see the <a :href="fullDonationPageURL" target="_blank" :title="fullDonationPageTitle" class="underline">full donation page</a>.</p>
                    </label>
                </div>
            </div>
            <div class="flex-1"></div>
            <Button :disabled="loading" label="Next step" class="mt-4" @click="nextStep"/>
        </div>
    </div>
</template>

<script setup>
import debounce from "lodash.debounce";
import DonationValues from "@/components/DonationValues.vue";
import Button from "@/components/Button.vue";
import {computed, onMounted, onUnmounted, ref} from "vue";
import {useAPI} from "@/composables/useAPI";
import {activeDomain} from "@/plugins/axios";
import {useDonationStore} from "@/store/useDonationStore";
import {storeToRefs} from "pinia";

const { getCustomDonationFee } = useAPI();
const {
    selectedDonation,
    isCustomDonation,
    userSupportFees,
    organizationSlug,
    predefinedDonations: donations,
    customDonationValue,
    selectedDonationFrequency
} = storeToRefs(useDonationStore());

const {nextStep} = useDonationStore();

const props = defineProps({
    organizationId: {
        type: String,
        required: true,
    },
    loading: {
        type: Boolean,
        default: false,
    }
})

function selectDonation(donation) {
    selectedDonation.value = {...donation}
    customDonationValue.value = null;
}

function updateWidth() {
    bodyWidth.value = document.body.clientWidth;
}

const selectCustomValue = debounce(async (event) => {
    const amount = parseInt(event.target.value) || 0

    const req = await getCustomDonationFee(props.organizationId, amount);

    selectedDonation.value = {amount: amount, transaction_fee: req.fee, isCustomDonation: true};
}, 100);

const transactionFee = computed(() => {
    if(!selectedDonation.value) {
        return 0
    }

    if(selectedDonationFrequency.value.id === 2 && Object.hasOwn(selectedDonation.value, 'transaction_recurring_fee')) {
        return '$' + selectedDonation.value.transaction_recurring_fee;
    }

    return '$' + selectedDonation.value.transaction_fee;
})

const fullDonationPageURL = computed(() => {
    if(!organizationSlug.value) {
        return '';
    }
    return window.location.protocol + '//' + organizationSlug.value + activeDomain;
})

const fullDonationPageTitle = computed(() => {
    if(!organizationSlug.value) {
        return '';
    }
    return organizationSlug.value + ' donation page';
})

const bodyWidth = ref(0);

const styleClasses = computed(() => ({
    titlePadding: {
        'px-8': bodyWidth.value < 390,
        'px-5': bodyWidth.value >= 390,
    },
    contentPadding: {
        'pr-2 pl-2 pb-4': bodyWidth.value <= 410,
        'pr-4 pl-4 pb-4': bodyWidth.value > 410
    },
    donationValuesWrap: {
        'justify-between': bodyWidth.value <= 390,
        '': bodyWidth.value > 390
    },
    preloaderMargin: {
        'ml-2': bodyWidth.value < 390,
        'ml-4': bodyWidth.value >= 390
    },
    inputWrap: {
        'pl-0': donations.value.length % 3 === 0,
        'pl-2': bodyWidth.value < 390 && donations.value.length % 3 !== 0,
        'pl-4': bodyWidth.value >= 390 && donations.value.length % 3 !== 0,
    },
    input: {
        'h-[70px]': bodyWidth.value < 390,
        'h-11': bodyWidth.value >= 390
    },
    inputCurrency: {
        'right-2 text-left': bodyWidth.value < 390,
        'right-6 text-center': bodyWidth.value >= 390
    }
}));

const showDonationFrequencyLabel = computed(() => {
    if(!selectedDonationFrequency.value) {
        return false
    }

    return selectedDonationFrequency.value.frequency_label.toLowerCase() === 'monthly';
});

onMounted(() => {
    updateWidth();
    window.addEventListener('resize', updateWidth);
})

onUnmounted(() => {
    window.removeEventListener('resize', updateWidth);
})
</script>

<style>

/* Hide the browser's default checkbox */
.container input {
    position: absolute;
    opacity: 0;
    cursor: pointer;
    height: 0;
    width: 0;
}

/* Create a custom checkbox */
.checkmark {
    height: 16px;
    width: 16px;
    border-radius: 2px;
    background-color: #fff;
    border: 1px solid #ccc;
    display: block;
    position: relative;
}

/* On mouse-over, add a grey background color */
.container:hover input ~ .checkmark {
    @apply bg-primary-1 border-primary-1 border;
}

/* When the checkbox is checked, add a blue background */
.container input:checked ~ .checkmark {
    @apply bg-primary-1 border-primary-1 border;
}

/* Create the checkmark/indicator (hidden when not checked) */
.checkmark:after {
    content: "";
    display: none;
}

/* Show the checkmark when checked */
.container input:checked ~ .checkmark:after {
    display: block;
}

/* Style the checkmark/indicator */
.container .checkmark:after {
    left: 50%;
    top: 50%;
    position: absolute;
    width: 4px;
    height: 8px;
    border: solid white;
    border-width: 0 2px 2px 0;
    transform: translate(-50%, -60%) rotate(45deg);
}
</style>